/**
 * OWL2 Turtle Parser
 *
 * Parses Turtle (Terse RDF Triple Language) format into OWL2 Ontology.
 * Uses N3.js for RDF parsing.
 */
import { Parser, Store, DataFactory } from 'n3';
import { fullIRI } from '../types/IRI';
import { typedLiteral, plainLiteral } from '../types/Literal';
import * as factories from '../factories/index';
import { OWL_NAMESPACE, RDF_NAMESPACE, RDFS_NAMESPACE, XSD_NAMESPACE } from '../xml/constants';
const { namedNode } = DataFactory;
// Semantical Visual namespace for styling annotations
const SEM_NAMESPACE = 'http://semantical.io/ontology/visual#';
// SKOS namespace for concept labels
const SKOS_NAMESPACE = 'http://www.w3.org/2004/02/skos/core#';
// Common RDF/OWL terms as strings for comparison
const RDF_TYPE = `${RDF_NAMESPACE}type`;
const RDF_FIRST = `${RDF_NAMESPACE}first`;
const RDF_REST = `${RDF_NAMESPACE}rest`;
const RDF_NIL = `${RDF_NAMESPACE}nil`;
const RDFS_SUBCLASS_OF = `${RDFS_NAMESPACE}subClassOf`;
const RDFS_SUBPROPERTY_OF = `${RDFS_NAMESPACE}subPropertyOf`;
const RDFS_DOMAIN = `${RDFS_NAMESPACE}domain`;
const RDFS_RANGE = `${RDFS_NAMESPACE}range`;
const RDFS_DATATYPE = `${RDFS_NAMESPACE}Datatype`;
const RDFS_CLASS = `${RDFS_NAMESPACE}Class`;
const RDFS_LITERAL = `${RDFS_NAMESPACE}Literal`;
const RDF_PROPERTY = `${RDF_NAMESPACE}Property`;
const OWL_ONTOLOGY = `${OWL_NAMESPACE}Ontology`;
const OWL_CLASS = `${OWL_NAMESPACE}Class`;
const OWL_OBJECT_PROPERTY = `${OWL_NAMESPACE}ObjectProperty`;
const OWL_DATATYPE_PROPERTY = `${OWL_NAMESPACE}DatatypeProperty`;
const OWL_ANNOTATION_PROPERTY = `${OWL_NAMESPACE}AnnotationProperty`;
const OWL_NAMED_INDIVIDUAL = `${OWL_NAMESPACE}NamedIndividual`;
const OWL_RESTRICTION = `${OWL_NAMESPACE}Restriction`;
const OWL_THING = `${OWL_NAMESPACE}Thing`;
const OWL_NOTHING = `${OWL_NAMESPACE}Nothing`;
const OWL_EQUIVALENT_CLASS = `${OWL_NAMESPACE}equivalentClass`;
const OWL_DISJOINT_WITH = `${OWL_NAMESPACE}disjointWith`;
const OWL_DISJOINT_UNION_OF = `${OWL_NAMESPACE}disjointUnionOf`;
const OWL_ALL_DISJOINT_CLASSES = `${OWL_NAMESPACE}AllDisjointClasses`;
const OWL_MEMBERS = `${OWL_NAMESPACE}members`;
const OWL_EQUIVALENT_PROPERTY = `${OWL_NAMESPACE}equivalentProperty`;
const OWL_PROPERTY_DISJOINT_WITH = `${OWL_NAMESPACE}propertyDisjointWith`;
const OWL_ALL_DISJOINT_PROPERTIES = `${OWL_NAMESPACE}AllDisjointProperties`;
const OWL_INVERSE_OF = `${OWL_NAMESPACE}inverseOf`;
const OWL_FUNCTIONAL_PROPERTY = `${OWL_NAMESPACE}FunctionalProperty`;
const OWL_INVERSE_FUNCTIONAL_PROPERTY = `${OWL_NAMESPACE}InverseFunctionalProperty`;
const OWL_REFLEXIVE_PROPERTY = `${OWL_NAMESPACE}ReflexiveProperty`;
const OWL_IRREFLEXIVE_PROPERTY = `${OWL_NAMESPACE}IrreflexiveProperty`;
const OWL_SYMMETRIC_PROPERTY = `${OWL_NAMESPACE}SymmetricProperty`;
const OWL_ASYMMETRIC_PROPERTY = `${OWL_NAMESPACE}AsymmetricProperty`;
const OWL_TRANSITIVE_PROPERTY = `${OWL_NAMESPACE}TransitiveProperty`;
const OWL_ON_PROPERTY = `${OWL_NAMESPACE}onProperty`;
const OWL_SOME_VALUES_FROM = `${OWL_NAMESPACE}someValuesFrom`;
const OWL_ALL_VALUES_FROM = `${OWL_NAMESPACE}allValuesFrom`;
const OWL_HAS_VALUE = `${OWL_NAMESPACE}hasValue`;
const OWL_HAS_SELF = `${OWL_NAMESPACE}hasSelf`;
const OWL_MIN_CARDINALITY = `${OWL_NAMESPACE}minCardinality`;
const OWL_MAX_CARDINALITY = `${OWL_NAMESPACE}maxCardinality`;
const OWL_CARDINALITY = `${OWL_NAMESPACE}cardinality`;
const OWL_MIN_QUALIFIED_CARDINALITY = `${OWL_NAMESPACE}minQualifiedCardinality`;
const OWL_MAX_QUALIFIED_CARDINALITY = `${OWL_NAMESPACE}maxQualifiedCardinality`;
const OWL_QUALIFIED_CARDINALITY = `${OWL_NAMESPACE}qualifiedCardinality`;
const OWL_ON_CLASS = `${OWL_NAMESPACE}onClass`;
const OWL_ON_DATA_RANGE = `${OWL_NAMESPACE}onDataRange`;
const OWL_INTERSECTION_OF = `${OWL_NAMESPACE}intersectionOf`;
const OWL_UNION_OF = `${OWL_NAMESPACE}unionOf`;
const OWL_COMPLEMENT_OF = `${OWL_NAMESPACE}complementOf`;
const OWL_ONE_OF = `${OWL_NAMESPACE}oneOf`;
const OWL_DATATYPE_COMPLEMENT_OF = `${OWL_NAMESPACE}datatypeComplementOf`;
const OWL_ON_DATATYPE = `${OWL_NAMESPACE}onDatatype`;
const OWL_WITH_RESTRICTIONS = `${OWL_NAMESPACE}withRestrictions`;
const OWL_PROPERTY_CHAIN_AXIOM = `${OWL_NAMESPACE}propertyChainAxiom`;
const OWL_SAME_AS = `${OWL_NAMESPACE}sameAs`;
const OWL_DIFFERENT_FROM = `${OWL_NAMESPACE}differentFrom`;
const OWL_ALL_DIFFERENT = `${OWL_NAMESPACE}AllDifferent`;
const OWL_DISTINCT_MEMBERS = `${OWL_NAMESPACE}distinctMembers`;
const OWL_IMPORTS = `${OWL_NAMESPACE}imports`;
const OWL_VERSION_IRI = `${OWL_NAMESPACE}versionIRI`;
const OWL_NEGATIVE_PROPERTY_ASSERTION = `${OWL_NAMESPACE}NegativePropertyAssertion`;
const OWL_SOURCE_INDIVIDUAL = `${OWL_NAMESPACE}sourceIndividual`;
const OWL_ASSERTION_PROPERTY = `${OWL_NAMESPACE}assertionProperty`;
const OWL_TARGET_INDIVIDUAL = `${OWL_NAMESPACE}targetIndividual`;
const OWL_TARGET_VALUE = `${OWL_NAMESPACE}targetValue`;
const OWL_HAS_KEY = `${OWL_NAMESPACE}hasKey`;
/**
 * Parses Turtle format into an OWL2 Ontology.
 */
export class Owl2TurtleParser {
    constructor() {
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "prefixes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "boundedContext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
    }
    /**
     * Parse Turtle string to Ontology (async).
     * Uses N3's callback API to capture both quads and prefixes.
     *
     * @param turtle - The Turtle content to parse
     * @param options - Optional parsing options including boundedContext for resolving external references
     */
    parse(turtle, options) {
        return new Promise((resolve, reject) => {
            this.store = new Store();
            this.prefixes = new Map();
            this.boundedContext = options?.boundedContext;
            const parser = new Parser();
            parser.parse(turtle, (error, quad, prefixes) => {
                if (error) {
                    reject(error);
                    return;
                }
                if (quad) {
                    this.store.addQuad(quad);
                }
                if (prefixes) {
                    // N3 calls callback with prefixes object when parsing completes
                    for (const [prefix, namespaceNode] of Object.entries(prefixes)) {
                        // namespaceNode can be string or NamedNode depending on N3 version
                        const namespace = typeof namespaceNode === 'string'
                            ? namespaceNode
                            : namespaceNode.value;
                        this.prefixes.set(prefix, namespace);
                    }
                    // Ensure standard prefixes are present
                    if (!this.prefixes.has('owl'))
                        this.prefixes.set('owl', OWL_NAMESPACE);
                    if (!this.prefixes.has('rdf'))
                        this.prefixes.set('rdf', RDF_NAMESPACE);
                    if (!this.prefixes.has('rdfs'))
                        this.prefixes.set('rdfs', RDFS_NAMESPACE);
                    if (!this.prefixes.has('xsd'))
                        this.prefixes.set('xsd', XSD_NAMESPACE);
                    // Build and resolve ontology when parsing is complete
                    resolve(this.buildOntology());
                }
            });
        });
    }
    /**
     * Parse from a pre-populated N3.Store to Ontology.
     * This allows reusing the OWL2 axiom reconstruction logic with RDF parsed
     * from other sources (e.g., RDF/XML via rdfxml-streaming-parser).
     *
     * @param store - N3.Store containing the RDF quads
     * @param prefixes - Map of prefix name to namespace IRI
     * @param options - Optional parsing options including boundedContext for resolving external references
     */
    parseFromStore(store, prefixes, options) {
        this.store = store;
        this.prefixes = new Map(prefixes);
        this.boundedContext = options?.boundedContext;
        // Ensure standard prefixes are present
        if (!this.prefixes.has('owl'))
            this.prefixes.set('owl', OWL_NAMESPACE);
        if (!this.prefixes.has('rdf'))
            this.prefixes.set('rdf', RDF_NAMESPACE);
        if (!this.prefixes.has('rdfs'))
            this.prefixes.set('rdfs', RDFS_NAMESPACE);
        if (!this.prefixes.has('xsd'))
            this.prefixes.set('xsd', XSD_NAMESPACE);
        return this.buildOntology();
    }
    buildOntology() {
        const axioms = [];
        const prefixDeclarations = [];
        // Convert prefixes to declarations
        for (const [name, iri] of this.prefixes) {
            prefixDeclarations.push(factories.prefixDeclaration(name, iri));
        }
        // Find ontology IRI
        let ontologyIRI;
        let versionIRI;
        const imports = [];
        const annotations = [];
        const ontologyQuads = this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_ONTOLOGY), null);
        if (ontologyQuads.length > 0) {
            const ontoSubject = ontologyQuads[0].subject;
            if (ontoSubject.termType === 'NamedNode') {
                ontologyIRI = fullIRI(ontoSubject.value);
            }
            // Version IRI
            const versionQuads = this.store.getQuads(ontoSubject, namedNode(OWL_VERSION_IRI), null, null);
            if (versionQuads.length > 0 && versionQuads[0].object.termType === 'NamedNode') {
                versionIRI = fullIRI(versionQuads[0].object.value);
            }
            // Imports
            const importQuads = this.store.getQuads(ontoSubject, namedNode(OWL_IMPORTS), null, null);
            for (const q of importQuads) {
                if (q.object.termType === 'NamedNode') {
                    imports.push(fullIRI(q.object.value));
                }
            }
        }
        // Fallback: For RDFS ontologies without owl:Ontology declaration,
        // try to infer ontology IRI from dcterms:title or rdfs:isDefinedBy subjects
        if (!ontologyIRI) {
            const DCTERMS_TITLE = 'http://purl.org/dc/terms/title';
            const titleQuads = this.store.getQuads(null, namedNode(DCTERMS_TITLE), null, null);
            if (titleQuads.length > 0 && titleQuads[0].subject.termType === 'NamedNode') {
                ontologyIRI = fullIRI(titleQuads[0].subject.value);
            }
        }
        // Parse declarations
        this.parseDeclarations(axioms);
        // Parse class axioms
        this.parseSubClassOfAxioms(axioms);
        this.parseEquivalentClassesAxioms(axioms);
        this.parseDisjointClassesAxioms(axioms);
        this.parseDisjointUnionAxioms(axioms);
        // Parse object property axioms
        this.parseSubObjectPropertyOfAxioms(axioms);
        this.parseEquivalentObjectPropertiesAxioms(axioms);
        this.parseDisjointObjectPropertiesAxioms(axioms);
        this.parseInverseObjectPropertiesAxioms(axioms);
        this.parseObjectPropertyDomainAxioms(axioms);
        this.parseObjectPropertyRangeAxioms(axioms);
        this.parseObjectPropertyCharacteristics(axioms);
        // Parse data property axioms
        this.parseSubDataPropertyOfAxioms(axioms);
        this.parseEquivalentDataPropertiesAxioms(axioms);
        this.parseDisjointDataPropertiesAxioms(axioms);
        this.parseDataPropertyDomainAxioms(axioms);
        this.parseDataPropertyRangeAxioms(axioms);
        this.parseFunctionalDataPropertyAxioms(axioms);
        // Parse HasKey axioms
        this.parseHasKeyAxioms(axioms);
        // Parse assertions
        this.parseSameIndividualAxioms(axioms);
        this.parseDifferentIndividualsAxioms(axioms);
        this.parseClassAssertions(axioms);
        this.parseObjectPropertyAssertions(axioms);
        this.parseDataPropertyAssertions(axioms);
        this.parseNegativePropertyAssertions(axioms);
        // Parse annotation axioms
        this.parseAnnotationAssertions(axioms);
        this.parseSubAnnotationPropertyOfAxioms(axioms);
        this.parseAnnotationPropertyDomainAxioms(axioms);
        this.parseAnnotationPropertyRangeAxioms(axioms);
        return {
            ...(ontologyIRI && { ontologyIRI }),
            ...(versionIRI && { versionIRI }),
            prefixes: prefixDeclarations,
            imports: imports.map(iri => factories.importDeclaration(iri)),
            annotations,
            axioms
        };
    }
    // =========================================================================
    // Declaration Parsing
    // =========================================================================
    parseDeclarations(axioms) {
        // Track declared classes and properties to avoid duplicates
        const declaredClasses = new Set();
        const declaredObjectProps = new Set();
        const declaredDataProps = new Set();
        // OWL Classes (owl:Class)
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_CLASS), null)) {
            if (q.subject.termType === 'NamedNode') {
                const iri = fullIRI(q.subject.value);
                if (!this.isBuiltIn(q.subject.value) && !declaredClasses.has(q.subject.value)) {
                    axioms.push(factories.declaration(factories.classEntity(iri)));
                    declaredClasses.add(q.subject.value);
                }
            }
        }
        // RDFS Classes (rdfs:Class)
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(RDFS_CLASS), null)) {
            if (q.subject.termType === 'NamedNode') {
                const iri = fullIRI(q.subject.value);
                if (!this.isBuiltIn(q.subject.value) && !declaredClasses.has(q.subject.value)) {
                    axioms.push(factories.declaration(factories.classEntity(iri)));
                    declaredClasses.add(q.subject.value);
                }
            }
        }
        // OWL Object Properties (owl:ObjectProperty)
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_OBJECT_PROPERTY), null)) {
            if (q.subject.termType === 'NamedNode' && !declaredObjectProps.has(q.subject.value)) {
                const iri = fullIRI(q.subject.value);
                axioms.push(factories.declaration(factories.objectPropertyEntity(iri)));
                declaredObjectProps.add(q.subject.value);
            }
        }
        // OWL Data Properties (owl:DatatypeProperty)
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_DATATYPE_PROPERTY), null)) {
            if (q.subject.termType === 'NamedNode' && !declaredDataProps.has(q.subject.value)) {
                const iri = fullIRI(q.subject.value);
                axioms.push(factories.declaration(factories.dataPropertyEntity(iri)));
                declaredDataProps.add(q.subject.value);
            }
        }
        // RDF Properties (rdf:Property) - infer type from range
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(RDF_PROPERTY), null)) {
            if (q.subject.termType === 'NamedNode' &&
                !declaredObjectProps.has(q.subject.value) &&
                !declaredDataProps.has(q.subject.value) &&
                !this.isBuiltIn(q.subject.value)) {
                const iri = fullIRI(q.subject.value);
                // Infer property type from range
                if (this.hasDataRange(q.subject)) {
                    axioms.push(factories.declaration(factories.dataPropertyEntity(iri)));
                    declaredDataProps.add(q.subject.value);
                }
                else {
                    axioms.push(factories.declaration(factories.objectPropertyEntity(iri)));
                    declaredObjectProps.add(q.subject.value);
                }
            }
        }
        // Annotation Properties
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_ANNOTATION_PROPERTY), null)) {
            if (q.subject.termType === 'NamedNode') {
                const iri = fullIRI(q.subject.value);
                axioms.push(factories.declaration(factories.annotationPropertyEntity(iri)));
            }
        }
        // Named Individuals
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_NAMED_INDIVIDUAL), null)) {
            if (q.subject.termType === 'NamedNode') {
                const iri = fullIRI(q.subject.value);
                axioms.push(factories.declaration(factories.namedIndividualEntity(iri)));
            }
        }
        // Datatypes
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(RDFS_DATATYPE), null)) {
            if (q.subject.termType === 'NamedNode' && !this.isBuiltInDatatype(q.subject.value)) {
                const iri = fullIRI(q.subject.value);
                axioms.push(factories.declaration(factories.datatypeEntity(iri)));
            }
        }
    }
    isBuiltIn(iri) {
        return iri === OWL_THING || iri === OWL_NOTHING ||
            iri.startsWith(OWL_NAMESPACE) ||
            iri.startsWith(RDF_NAMESPACE) ||
            iri.startsWith(RDFS_NAMESPACE);
    }
    isBuiltInDatatype(iri) {
        return iri.startsWith(XSD_NAMESPACE) ||
            iri.startsWith(RDF_NAMESPACE) ||
            iri === `${OWL_NAMESPACE}real` ||
            iri === `${OWL_NAMESPACE}rational`;
    }
    /**
     * Check if a property has a data range (i.e., should be treated as a DataProperty).
     * Returns true if the property has rdfs:range pointing to a datatype.
     */
    hasDataRange(term) {
        const rangeQuads = this.store.getQuads(term, namedNode(RDFS_RANGE), null, null);
        for (const q of rangeQuads) {
            if (q.object.termType === 'NamedNode') {
                const rangeIri = q.object.value;
                // Check if range is a datatype (XSD namespace, rdfs:Literal, or declared as rdfs:Datatype)
                if (rangeIri.startsWith(XSD_NAMESPACE) ||
                    rangeIri === RDFS_LITERAL ||
                    this.store.getQuads(q.object, namedNode(RDF_TYPE), namedNode(RDFS_DATATYPE), null).length > 0) {
                    return true;
                }
            }
        }
        return false;
    }
    // =========================================================================
    // Class Axiom Parsing
    // =========================================================================
    parseSubClassOfAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDFS_SUBCLASS_OF), null, null)) {
            try {
                const subClass = this.parseClassExpression(q.subject);
                const superClass = this.parseClassExpression(q.object);
                axioms.push(factories.subClassOf(subClass, superClass));
            }
            catch {
                // Skip if we can't parse the class expressions
            }
        }
    }
    parseEquivalentClassesAxioms(axioms) {
        const processed = new Set();
        for (const q of this.store.getQuads(null, namedNode(OWL_EQUIVALENT_CLASS), null, null)) {
            const key = `${this.termToKey(q.subject)}-${this.termToKey(q.object)}`;
            const reverseKey = `${this.termToKey(q.object)}-${this.termToKey(q.subject)}`;
            if (processed.has(key) || processed.has(reverseKey))
                continue;
            processed.add(key);
            try {
                const class1 = this.parseClassExpression(q.subject);
                const class2 = this.parseClassExpression(q.object);
                axioms.push(factories.equivalentClasses(class1, class2));
            }
            catch {
                // Skip if we can't parse
            }
        }
    }
    parseDisjointClassesAxioms(axioms) {
        // Binary disjointness
        const processed = new Set();
        for (const q of this.store.getQuads(null, namedNode(OWL_DISJOINT_WITH), null, null)) {
            const key = `${this.termToKey(q.subject)}-${this.termToKey(q.object)}`;
            const reverseKey = `${this.termToKey(q.object)}-${this.termToKey(q.subject)}`;
            if (processed.has(key) || processed.has(reverseKey))
                continue;
            processed.add(key);
            try {
                const class1 = this.parseClassExpression(q.subject);
                const class2 = this.parseClassExpression(q.object);
                axioms.push(factories.disjointClasses(class1, class2));
            }
            catch {
                // Skip
            }
        }
        // N-ary disjointness (AllDisjointClasses)
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_ALL_DISJOINT_CLASSES), null)) {
            const membersQuads = this.store.getQuads(q.subject, namedNode(OWL_MEMBERS), null, null);
            if (membersQuads.length > 0) {
                try {
                    const list = this.parseRdfList(membersQuads[0].object);
                    const classes = list.map(t => this.parseClassExpression(t));
                    if (classes.length >= 2) {
                        axioms.push(factories.disjointClasses(classes[0], classes[1], ...classes.slice(2)));
                    }
                }
                catch {
                    // Skip
                }
            }
        }
    }
    parseDisjointUnionAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(OWL_DISJOINT_UNION_OF), null, null)) {
            if (q.subject.termType === 'NamedNode') {
                try {
                    const disjointClass = factories.owlClass(fullIRI(q.subject.value));
                    const list = this.parseRdfList(q.object);
                    const classes = list.map(t => this.parseClassExpression(t));
                    if (classes.length >= 2) {
                        axioms.push(factories.disjointUnion(disjointClass, classes[0], classes[1], ...classes.slice(2)));
                    }
                }
                catch {
                    // Skip
                }
            }
        }
    }
    // =========================================================================
    // Object Property Axiom Parsing
    // =========================================================================
    parseSubObjectPropertyOfAxioms(axioms) {
        // Regular sub-property
        for (const q of this.store.getQuads(null, namedNode(RDFS_SUBPROPERTY_OF), null, null)) {
            if (this.isObjectProperty(q.subject) && this.isObjectProperty(q.object)) {
                try {
                    const subProp = this.parseObjectPropertyExpression(q.subject);
                    const superProp = this.parseObjectPropertyExpression(q.object);
                    axioms.push(factories.subObjectPropertyOf(subProp, superProp));
                }
                catch {
                    // Skip
                }
            }
        }
        // Property chains
        for (const q of this.store.getQuads(null, namedNode(OWL_PROPERTY_CHAIN_AXIOM), null, null)) {
            try {
                const superProp = this.parseObjectPropertyExpression(q.subject);
                const chainList = this.parseRdfList(q.object);
                const chain = chainList.map(t => this.parseObjectPropertyExpression(t));
                if (chain.length >= 2) {
                    const chainExpr = {
                        type: 'ObjectPropertyChain',
                        properties: chain
                    };
                    axioms.push(factories.subObjectPropertyOf(chainExpr, superProp));
                }
            }
            catch {
                // Skip
            }
        }
    }
    parseEquivalentObjectPropertiesAxioms(axioms) {
        const processed = new Set();
        for (const q of this.store.getQuads(null, namedNode(OWL_EQUIVALENT_PROPERTY), null, null)) {
            if (!this.isObjectProperty(q.subject))
                continue;
            const key = `${this.termToKey(q.subject)}-${this.termToKey(q.object)}`;
            const reverseKey = `${this.termToKey(q.object)}-${this.termToKey(q.subject)}`;
            if (processed.has(key) || processed.has(reverseKey))
                continue;
            processed.add(key);
            try {
                const prop1 = this.parseObjectPropertyExpression(q.subject);
                const prop2 = this.parseObjectPropertyExpression(q.object);
                axioms.push(factories.equivalentObjectProperties(prop1, prop2));
            }
            catch {
                // Skip
            }
        }
    }
    parseDisjointObjectPropertiesAxioms(axioms) {
        // Binary
        const processed = new Set();
        for (const q of this.store.getQuads(null, namedNode(OWL_PROPERTY_DISJOINT_WITH), null, null)) {
            if (!this.isObjectProperty(q.subject))
                continue;
            const key = `${this.termToKey(q.subject)}-${this.termToKey(q.object)}`;
            const reverseKey = `${this.termToKey(q.object)}-${this.termToKey(q.subject)}`;
            if (processed.has(key) || processed.has(reverseKey))
                continue;
            processed.add(key);
            try {
                const prop1 = this.parseObjectPropertyExpression(q.subject);
                const prop2 = this.parseObjectPropertyExpression(q.object);
                axioms.push(factories.disjointObjectProperties(prop1, prop2));
            }
            catch {
                // Skip
            }
        }
        // N-ary (AllDisjointProperties for object properties)
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_ALL_DISJOINT_PROPERTIES), null)) {
            const membersQuads = this.store.getQuads(q.subject, namedNode(OWL_MEMBERS), null, null);
            if (membersQuads.length > 0) {
                try {
                    const list = this.parseRdfList(membersQuads[0].object);
                    if (list.length > 0 && this.isObjectProperty(list[0])) {
                        const props = list.map(t => this.parseObjectPropertyExpression(t));
                        if (props.length >= 2) {
                            axioms.push(factories.disjointObjectProperties(props[0], props[1], ...props.slice(2)));
                        }
                    }
                }
                catch {
                    // Skip
                }
            }
        }
    }
    parseInverseObjectPropertiesAxioms(axioms) {
        const processed = new Set();
        for (const q of this.store.getQuads(null, namedNode(OWL_INVERSE_OF), null, null)) {
            // Only named property inverses (not blank node inverses which are ObjectInverseOf expressions)
            if (q.subject.termType !== 'NamedNode' || q.object.termType !== 'NamedNode')
                continue;
            const key = `${q.subject.value}-${q.object.value}`;
            const reverseKey = `${q.object.value}-${q.subject.value}`;
            if (processed.has(key) || processed.has(reverseKey))
                continue;
            processed.add(key);
            try {
                const prop1 = this.parseObjectPropertyExpression(q.subject);
                const prop2 = this.parseObjectPropertyExpression(q.object);
                axioms.push(factories.inverseObjectProperties(prop1, prop2));
            }
            catch {
                // Skip
            }
        }
    }
    parseObjectPropertyDomainAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDFS_DOMAIN), null, null)) {
            if (!this.isObjectProperty(q.subject))
                continue;
            try {
                const prop = this.parseObjectPropertyExpression(q.subject);
                const domain = this.parseClassExpression(q.object);
                axioms.push(factories.objectPropertyDomainAxiom(prop, domain));
            }
            catch {
                // Skip
            }
        }
    }
    parseObjectPropertyRangeAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDFS_RANGE), null, null)) {
            if (!this.isObjectProperty(q.subject))
                continue;
            // Check if range is a class (not a datatype)
            if (this.isDatatype(q.object))
                continue;
            try {
                const prop = this.parseObjectPropertyExpression(q.subject);
                const range = this.parseClassExpression(q.object);
                axioms.push(factories.objectPropertyRangeAxiom(prop, range));
            }
            catch {
                // Skip
            }
        }
    }
    parseObjectPropertyCharacteristics(axioms) {
        const characteristics = [
            { type: OWL_FUNCTIONAL_PROPERTY, factory: factories.functionalObjectProperty },
            { type: OWL_INVERSE_FUNCTIONAL_PROPERTY, factory: factories.inverseFunctionalObjectProperty },
            { type: OWL_REFLEXIVE_PROPERTY, factory: factories.reflexiveObjectProperty },
            { type: OWL_IRREFLEXIVE_PROPERTY, factory: factories.irreflexiveObjectProperty },
            { type: OWL_SYMMETRIC_PROPERTY, factory: factories.symmetricObjectProperty },
            { type: OWL_ASYMMETRIC_PROPERTY, factory: factories.asymmetricObjectProperty },
            { type: OWL_TRANSITIVE_PROPERTY, factory: factories.transitiveObjectProperty },
        ];
        for (const { type, factory } of characteristics) {
            for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(type), null)) {
                if (!this.isObjectProperty(q.subject))
                    continue;
                try {
                    const prop = this.parseObjectPropertyExpression(q.subject);
                    axioms.push(factory(prop));
                }
                catch {
                    // Skip
                }
            }
        }
    }
    // =========================================================================
    // Data Property Axiom Parsing
    // =========================================================================
    parseSubDataPropertyOfAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDFS_SUBPROPERTY_OF), null, null)) {
            if (this.isDataProperty(q.subject) && this.isDataProperty(q.object)) {
                try {
                    const subProp = this.parseDataPropertyExpression(q.subject);
                    const superProp = this.parseDataPropertyExpression(q.object);
                    axioms.push(factories.subDataPropertyOf(subProp, superProp));
                }
                catch {
                    // Skip
                }
            }
        }
    }
    parseEquivalentDataPropertiesAxioms(axioms) {
        const processed = new Set();
        for (const q of this.store.getQuads(null, namedNode(OWL_EQUIVALENT_PROPERTY), null, null)) {
            if (!this.isDataProperty(q.subject))
                continue;
            const key = `${this.termToKey(q.subject)}-${this.termToKey(q.object)}`;
            const reverseKey = `${this.termToKey(q.object)}-${this.termToKey(q.subject)}`;
            if (processed.has(key) || processed.has(reverseKey))
                continue;
            processed.add(key);
            try {
                const prop1 = this.parseDataPropertyExpression(q.subject);
                const prop2 = this.parseDataPropertyExpression(q.object);
                axioms.push(factories.equivalentDataProperties(prop1, prop2));
            }
            catch {
                // Skip
            }
        }
    }
    parseDisjointDataPropertiesAxioms(axioms) {
        const processed = new Set();
        for (const q of this.store.getQuads(null, namedNode(OWL_PROPERTY_DISJOINT_WITH), null, null)) {
            if (!this.isDataProperty(q.subject))
                continue;
            const key = `${this.termToKey(q.subject)}-${this.termToKey(q.object)}`;
            const reverseKey = `${this.termToKey(q.object)}-${this.termToKey(q.subject)}`;
            if (processed.has(key) || processed.has(reverseKey))
                continue;
            processed.add(key);
            try {
                const prop1 = this.parseDataPropertyExpression(q.subject);
                const prop2 = this.parseDataPropertyExpression(q.object);
                axioms.push(factories.disjointDataProperties(prop1, prop2));
            }
            catch {
                // Skip
            }
        }
    }
    parseDataPropertyDomainAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDFS_DOMAIN), null, null)) {
            if (!this.isDataProperty(q.subject))
                continue;
            try {
                const prop = this.parseDataPropertyExpression(q.subject);
                const domain = this.parseClassExpression(q.object);
                axioms.push(factories.dataPropertyDomainAxiom(prop, domain));
            }
            catch {
                // Skip
            }
        }
    }
    parseDataPropertyRangeAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDFS_RANGE), null, null)) {
            if (!this.isDataProperty(q.subject))
                continue;
            try {
                const prop = this.parseDataPropertyExpression(q.subject);
                const range = this.parseDataRange(q.object);
                axioms.push(factories.dataPropertyRangeAxiom(prop, range));
            }
            catch {
                // Skip
            }
        }
    }
    parseFunctionalDataPropertyAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_FUNCTIONAL_PROPERTY), null)) {
            if (!this.isDataProperty(q.subject))
                continue;
            try {
                const prop = this.parseDataPropertyExpression(q.subject);
                axioms.push(factories.functionalDataProperty(prop));
            }
            catch {
                // Skip
            }
        }
    }
    // =========================================================================
    // HasKey Parsing
    // =========================================================================
    parseHasKeyAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(OWL_HAS_KEY), null, null)) {
            try {
                const classExpr = this.parseClassExpression(q.subject);
                const keyList = this.parseRdfList(q.object);
                const objProps = [];
                const dataProps = [];
                for (const term of keyList) {
                    if (this.isObjectProperty(term)) {
                        objProps.push(this.parseObjectPropertyExpression(term));
                    }
                    else if (this.isDataProperty(term)) {
                        dataProps.push(this.parseDataPropertyExpression(term));
                    }
                }
                axioms.push(factories.hasKey(classExpr, objProps, dataProps));
            }
            catch {
                // Skip
            }
        }
    }
    // =========================================================================
    // Assertion Parsing
    // =========================================================================
    parseSameIndividualAxioms(axioms) {
        const processed = new Set();
        for (const q of this.store.getQuads(null, namedNode(OWL_SAME_AS), null, null)) {
            const key = `${this.termToKey(q.subject)}-${this.termToKey(q.object)}`;
            const reverseKey = `${this.termToKey(q.object)}-${this.termToKey(q.subject)}`;
            if (processed.has(key) || processed.has(reverseKey))
                continue;
            processed.add(key);
            try {
                const ind1 = this.parseIndividual(q.subject);
                const ind2 = this.parseIndividual(q.object);
                axioms.push(factories.sameIndividual(ind1, ind2));
            }
            catch {
                // Skip
            }
        }
    }
    parseDifferentIndividualsAxioms(axioms) {
        // Binary
        const processed = new Set();
        for (const q of this.store.getQuads(null, namedNode(OWL_DIFFERENT_FROM), null, null)) {
            const key = `${this.termToKey(q.subject)}-${this.termToKey(q.object)}`;
            const reverseKey = `${this.termToKey(q.object)}-${this.termToKey(q.subject)}`;
            if (processed.has(key) || processed.has(reverseKey))
                continue;
            processed.add(key);
            try {
                const ind1 = this.parseIndividual(q.subject);
                const ind2 = this.parseIndividual(q.object);
                axioms.push(factories.differentIndividuals(ind1, ind2));
            }
            catch {
                // Skip
            }
        }
        // N-ary (AllDifferent)
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_ALL_DIFFERENT), null)) {
            const membersQuads = this.store.getQuads(q.subject, namedNode(OWL_DISTINCT_MEMBERS), null, null);
            if (membersQuads.length > 0) {
                try {
                    const list = this.parseRdfList(membersQuads[0].object);
                    const inds = list.map(t => this.parseIndividual(t));
                    if (inds.length >= 2) {
                        axioms.push(factories.differentIndividuals(inds[0], inds[1], ...inds.slice(2)));
                    }
                }
                catch {
                    // Skip
                }
            }
        }
    }
    parseClassAssertions(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), null, null)) {
            // Skip built-in types and declarations
            if (this.isBuiltInType(q.object))
                continue;
            try {
                const ind = this.parseIndividual(q.subject);
                const classExpr = this.parseClassExpression(q.object);
                axioms.push(factories.classAssertion(classExpr, ind));
            }
            catch {
                // Skip - might not be an individual or class
            }
        }
    }
    parseObjectPropertyAssertions(axioms) {
        // Get all object properties declared in THIS TTL
        const objPropIris = new Set();
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_OBJECT_PROPERTY), null)) {
            if (q.subject.termType === 'NamedNode') {
                objPropIris.add(q.subject.value);
            }
        }
        // Also include object properties from bounded context's loaded ontologies
        if (this.boundedContext) {
            for (const uri of this.boundedContext.getObjectPropertyUris()) {
                objPropIris.add(uri);
            }
        }
        for (const propIri of objPropIris) {
            for (const q of this.store.getQuads(null, namedNode(propIri), null, null)) {
                try {
                    const source = this.parseIndividual(q.subject);
                    const target = this.parseIndividual(q.object);
                    const prop = factories.objectProperty(fullIRI(propIri));
                    axioms.push(factories.objectPropertyAssertion(prop, source, target));
                }
                catch {
                    // Skip
                }
            }
        }
    }
    parseDataPropertyAssertions(axioms) {
        // Get all data properties declared in THIS TTL
        const dataPropIris = new Set();
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_DATATYPE_PROPERTY), null)) {
            if (q.subject.termType === 'NamedNode') {
                dataPropIris.add(q.subject.value);
            }
        }
        // Also include data properties from bounded context's loaded ontologies
        if (this.boundedContext) {
            for (const uri of this.boundedContext.getDataPropertyUris()) {
                dataPropIris.add(uri);
            }
        }
        for (const propIri of dataPropIris) {
            for (const q of this.store.getQuads(null, namedNode(propIri), null, null)) {
                if (q.object.termType !== 'Literal')
                    continue;
                try {
                    const source = this.parseIndividual(q.subject);
                    const value = this.parseLiteral(q.object);
                    const prop = factories.dataProperty(fullIRI(propIri));
                    axioms.push(factories.dataPropertyAssertion(prop, source, value));
                }
                catch {
                    // Skip
                }
            }
        }
    }
    parseNegativePropertyAssertions(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_NEGATIVE_PROPERTY_ASSERTION), null)) {
            try {
                const sourceQuads = this.store.getQuads(q.subject, namedNode(OWL_SOURCE_INDIVIDUAL), null, null);
                const propQuads = this.store.getQuads(q.subject, namedNode(OWL_ASSERTION_PROPERTY), null, null);
                const targetIndQuads = this.store.getQuads(q.subject, namedNode(OWL_TARGET_INDIVIDUAL), null, null);
                const targetValQuads = this.store.getQuads(q.subject, namedNode(OWL_TARGET_VALUE), null, null);
                if (sourceQuads.length === 0 || propQuads.length === 0)
                    continue;
                const source = this.parseIndividual(sourceQuads[0].object);
                if (targetIndQuads.length > 0) {
                    // Object property assertion
                    const target = this.parseIndividual(targetIndQuads[0].object);
                    const prop = this.parseObjectPropertyExpression(propQuads[0].object);
                    axioms.push(factories.negativeObjectPropertyAssertion(prop, source, target));
                }
                else if (targetValQuads.length > 0 && targetValQuads[0].object.termType === 'Literal') {
                    // Data property assertion
                    const value = this.parseLiteral(targetValQuads[0].object);
                    const prop = this.parseDataPropertyExpression(propQuads[0].object);
                    axioms.push(factories.negativeDataPropertyAssertion(prop, source, value));
                }
            }
            catch {
                // Skip
            }
        }
    }
    // =========================================================================
    // Annotation Axiom Parsing
    // =========================================================================
    parseAnnotationAssertions(axioms) {
        // Get all annotation properties
        const annPropIris = new Set();
        for (const q of this.store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_ANNOTATION_PROPERTY), null)) {
            if (q.subject.termType === 'NamedNode') {
                annPropIris.add(q.subject.value);
            }
        }
        // Add built-in annotation properties
        annPropIris.add(`${RDFS_NAMESPACE}label`);
        annPropIris.add(`${RDFS_NAMESPACE}comment`);
        annPropIris.add(`${RDFS_NAMESPACE}seeAlso`);
        annPropIris.add(`${RDFS_NAMESPACE}isDefinedBy`);
        // Add SKOS annotation properties
        annPropIris.add(`${SKOS_NAMESPACE}altLabel`);
        annPropIris.add(`${SKOS_NAMESPACE}prefLabel`);
        annPropIris.add(`${SKOS_NAMESPACE}definition`);
        // Add Dublin Core annotation properties
        annPropIris.add('http://purl.org/dc/terms/title'); // dcterms:title
        annPropIris.add('http://purl.org/dc/terms/description'); // dcterms:description
        annPropIris.add('http://purl.org/dc/elements/1.1/title'); // dc:title
        annPropIris.add('http://purl.org/dc/elements/1.1/description'); // dc:description
        // Add sem: visual annotation properties (for class/property styling)
        annPropIris.add(`${SEM_NAMESPACE}classType`);
        annPropIris.add(`${SEM_NAMESPACE}stereotype`);
        annPropIris.add(`${SEM_NAMESPACE}icon`);
        annPropIris.add(`${SEM_NAMESPACE}categoryColor`);
        annPropIris.add(`${SEM_NAMESPACE}relationshipType`);
        annPropIris.add(`${SEM_NAMESPACE}type`);
        annPropIris.add(`${SEM_NAMESPACE}sourceMarker`);
        annPropIris.add(`${SEM_NAMESPACE}targetMarker`);
        annPropIris.add(`${SEM_NAMESPACE}strokeStyle`);
        for (const propIri of annPropIris) {
            for (const q of this.store.getQuads(null, namedNode(propIri), null, null)) {
                try {
                    let subject;
                    if (q.subject.termType === 'NamedNode') {
                        subject = fullIRI(q.subject.value);
                    }
                    else if (q.subject.termType === 'BlankNode') {
                        subject = factories.anonymousIndividual(q.subject.value);
                    }
                    else {
                        continue;
                    }
                    const property = factories.annotationProperty(fullIRI(propIri));
                    let value;
                    if (q.object.termType === 'NamedNode') {
                        value = fullIRI(q.object.value);
                    }
                    else if (q.object.termType === 'BlankNode') {
                        value = factories.anonymousIndividual(q.object.value);
                    }
                    else if (q.object.termType === 'Literal') {
                        value = this.parseLiteral(q.object);
                    }
                    else {
                        continue;
                    }
                    axioms.push(factories.annotationAssertion(property, subject, value));
                }
                catch {
                    // Skip
                }
            }
        }
    }
    parseSubAnnotationPropertyOfAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDFS_SUBPROPERTY_OF), null, null)) {
            if (!this.isAnnotationProperty(q.subject) || !this.isAnnotationProperty(q.object))
                continue;
            if (q.subject.termType !== 'NamedNode' || q.object.termType !== 'NamedNode')
                continue;
            try {
                const subProp = factories.annotationProperty(fullIRI(q.subject.value));
                const superProp = factories.annotationProperty(fullIRI(q.object.value));
                axioms.push(factories.subAnnotationPropertyOf(subProp, superProp));
            }
            catch {
                // Skip
            }
        }
    }
    parseAnnotationPropertyDomainAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDFS_DOMAIN), null, null)) {
            if (!this.isAnnotationProperty(q.subject))
                continue;
            if (q.subject.termType !== 'NamedNode' || q.object.termType !== 'NamedNode')
                continue;
            try {
                const prop = factories.annotationProperty(fullIRI(q.subject.value));
                const domain = fullIRI(q.object.value);
                axioms.push(factories.annotationPropertyDomainAxiom(prop, domain));
            }
            catch {
                // Skip
            }
        }
    }
    parseAnnotationPropertyRangeAxioms(axioms) {
        for (const q of this.store.getQuads(null, namedNode(RDFS_RANGE), null, null)) {
            if (!this.isAnnotationProperty(q.subject))
                continue;
            if (q.subject.termType !== 'NamedNode' || q.object.termType !== 'NamedNode')
                continue;
            try {
                const prop = factories.annotationProperty(fullIRI(q.subject.value));
                const range = fullIRI(q.object.value);
                axioms.push(factories.annotationPropertyRangeAxiom(prop, range));
            }
            catch {
                // Skip
            }
        }
    }
    // =========================================================================
    // Class Expression Parsing
    // =========================================================================
    parseClassExpression(term) {
        if (term.termType === 'NamedNode') {
            return factories.owlClass(fullIRI(term.value));
        }
        if (term.termType !== 'BlankNode') {
            throw new Error('Invalid class expression term');
        }
        // Check for restriction
        const isRestriction = this.store.getQuads(term, namedNode(RDF_TYPE), namedNode(OWL_RESTRICTION), null).length > 0;
        if (isRestriction) {
            return this.parseRestriction(term);
        }
        // Check for boolean combinations
        const intersectionQuads = this.store.getQuads(term, namedNode(OWL_INTERSECTION_OF), null, null);
        if (intersectionQuads.length > 0) {
            const list = this.parseRdfList(intersectionQuads[0].object);
            const classes = list.map(t => this.parseClassExpression(t));
            if (classes.length >= 2) {
                return factories.objectIntersectionOf(classes[0], classes[1], ...classes.slice(2));
            }
        }
        const unionQuads = this.store.getQuads(term, namedNode(OWL_UNION_OF), null, null);
        if (unionQuads.length > 0) {
            const list = this.parseRdfList(unionQuads[0].object);
            const classes = list.map(t => this.parseClassExpression(t));
            if (classes.length >= 2) {
                return factories.objectUnionOf(classes[0], classes[1], ...classes.slice(2));
            }
        }
        const complementQuads = this.store.getQuads(term, namedNode(OWL_COMPLEMENT_OF), null, null);
        if (complementQuads.length > 0) {
            return factories.objectComplementOf(this.parseClassExpression(complementQuads[0].object));
        }
        const oneOfQuads = this.store.getQuads(term, namedNode(OWL_ONE_OF), null, null);
        if (oneOfQuads.length > 0) {
            const list = this.parseRdfList(oneOfQuads[0].object);
            const individuals = list.map(t => this.parseIndividual(t));
            if (individuals.length >= 1) {
                return factories.objectOneOf(individuals[0], ...individuals.slice(1));
            }
        }
        throw new Error('Unknown class expression type');
    }
    parseRestriction(bn) {
        const onPropertyQuads = this.store.getQuads(bn, namedNode(OWL_ON_PROPERTY), null, null);
        if (onPropertyQuads.length === 0) {
            throw new Error('Restriction without onProperty');
        }
        const propTerm = onPropertyQuads[0].object;
        // Determine if object or data property
        const isObjProp = this.isObjectProperty(propTerm);
        if (isObjProp) {
            const prop = this.parseObjectPropertyExpression(propTerm);
            // someValuesFrom
            const someQuads = this.store.getQuads(bn, namedNode(OWL_SOME_VALUES_FROM), null, null);
            if (someQuads.length > 0) {
                return factories.objectSomeValuesFrom(prop, this.parseClassExpression(someQuads[0].object));
            }
            // allValuesFrom
            const allQuads = this.store.getQuads(bn, namedNode(OWL_ALL_VALUES_FROM), null, null);
            if (allQuads.length > 0) {
                return factories.objectAllValuesFrom(prop, this.parseClassExpression(allQuads[0].object));
            }
            // hasValue
            const hasValueQuads = this.store.getQuads(bn, namedNode(OWL_HAS_VALUE), null, null);
            if (hasValueQuads.length > 0) {
                return factories.objectHasValue(prop, this.parseIndividual(hasValueQuads[0].object));
            }
            // hasSelf
            const hasSelfQuads = this.store.getQuads(bn, namedNode(OWL_HAS_SELF), null, null);
            if (hasSelfQuads.length > 0) {
                return factories.objectHasSelf(prop);
            }
            // Cardinality
            return this.parseObjectCardinality(bn, prop);
        }
        else {
            const prop = this.parseDataPropertyExpression(propTerm);
            // someValuesFrom
            const someQuads = this.store.getQuads(bn, namedNode(OWL_SOME_VALUES_FROM), null, null);
            if (someQuads.length > 0) {
                return factories.dataSomeValuesFrom([prop], this.parseDataRange(someQuads[0].object));
            }
            // allValuesFrom
            const allQuads = this.store.getQuads(bn, namedNode(OWL_ALL_VALUES_FROM), null, null);
            if (allQuads.length > 0) {
                return factories.dataAllValuesFrom([prop], this.parseDataRange(allQuads[0].object));
            }
            // hasValue
            const hasValueQuads = this.store.getQuads(bn, namedNode(OWL_HAS_VALUE), null, null);
            if (hasValueQuads.length > 0 && hasValueQuads[0].object.termType === 'Literal') {
                return factories.dataHasValue(prop, this.parseLiteral(hasValueQuads[0].object));
            }
            // Cardinality
            return this.parseDataCardinality(bn, prop);
        }
    }
    parseObjectCardinality(bn, prop) {
        const onClassQuads = this.store.getQuads(bn, namedNode(OWL_ON_CLASS), null, null);
        const filler = onClassQuads.length > 0 ? this.parseClassExpression(onClassQuads[0].object) : undefined;
        // Qualified or unqualified cardinality
        const minQualQuads = this.store.getQuads(bn, namedNode(OWL_MIN_QUALIFIED_CARDINALITY), null, null);
        const maxQualQuads = this.store.getQuads(bn, namedNode(OWL_MAX_QUALIFIED_CARDINALITY), null, null);
        const exactQualQuads = this.store.getQuads(bn, namedNode(OWL_QUALIFIED_CARDINALITY), null, null);
        const minQuads = this.store.getQuads(bn, namedNode(OWL_MIN_CARDINALITY), null, null);
        const maxQuads = this.store.getQuads(bn, namedNode(OWL_MAX_CARDINALITY), null, null);
        const exactQuads = this.store.getQuads(bn, namedNode(OWL_CARDINALITY), null, null);
        if (minQualQuads.length > 0) {
            const card = parseInt(minQualQuads[0].object.value, 10);
            return factories.objectMinCardinality(card, prop, filler);
        }
        if (maxQualQuads.length > 0) {
            const card = parseInt(maxQualQuads[0].object.value, 10);
            return factories.objectMaxCardinality(card, prop, filler);
        }
        if (exactQualQuads.length > 0) {
            const card = parseInt(exactQualQuads[0].object.value, 10);
            return factories.objectExactCardinality(card, prop, filler);
        }
        if (minQuads.length > 0) {
            const card = parseInt(minQuads[0].object.value, 10);
            return factories.objectMinCardinality(card, prop);
        }
        if (maxQuads.length > 0) {
            const card = parseInt(maxQuads[0].object.value, 10);
            return factories.objectMaxCardinality(card, prop);
        }
        if (exactQuads.length > 0) {
            const card = parseInt(exactQuads[0].object.value, 10);
            return factories.objectExactCardinality(card, prop);
        }
        throw new Error('Unknown restriction type');
    }
    parseDataCardinality(bn, prop) {
        const onDataRangeQuads = this.store.getQuads(bn, namedNode(OWL_ON_DATA_RANGE), null, null);
        const filler = onDataRangeQuads.length > 0 ? this.parseDataRange(onDataRangeQuads[0].object) : undefined;
        const minQualQuads = this.store.getQuads(bn, namedNode(OWL_MIN_QUALIFIED_CARDINALITY), null, null);
        const maxQualQuads = this.store.getQuads(bn, namedNode(OWL_MAX_QUALIFIED_CARDINALITY), null, null);
        const exactQualQuads = this.store.getQuads(bn, namedNode(OWL_QUALIFIED_CARDINALITY), null, null);
        const minQuads = this.store.getQuads(bn, namedNode(OWL_MIN_CARDINALITY), null, null);
        const maxQuads = this.store.getQuads(bn, namedNode(OWL_MAX_CARDINALITY), null, null);
        const exactQuads = this.store.getQuads(bn, namedNode(OWL_CARDINALITY), null, null);
        if (minQualQuads.length > 0) {
            const card = parseInt(minQualQuads[0].object.value, 10);
            return factories.dataMinCardinality(card, prop, filler);
        }
        if (maxQualQuads.length > 0) {
            const card = parseInt(maxQualQuads[0].object.value, 10);
            return factories.dataMaxCardinality(card, prop, filler);
        }
        if (exactQualQuads.length > 0) {
            const card = parseInt(exactQualQuads[0].object.value, 10);
            return factories.dataExactCardinality(card, prop, filler);
        }
        if (minQuads.length > 0) {
            const card = parseInt(minQuads[0].object.value, 10);
            return factories.dataMinCardinality(card, prop);
        }
        if (maxQuads.length > 0) {
            const card = parseInt(maxQuads[0].object.value, 10);
            return factories.dataMaxCardinality(card, prop);
        }
        if (exactQuads.length > 0) {
            const card = parseInt(exactQuads[0].object.value, 10);
            return factories.dataExactCardinality(card, prop);
        }
        throw new Error('Unknown data restriction type');
    }
    // =========================================================================
    // Data Range Parsing
    // =========================================================================
    parseDataRange(term) {
        if (term.termType === 'NamedNode') {
            return factories.datatype(fullIRI(term.value));
        }
        if (term.termType !== 'BlankNode') {
            throw new Error('Invalid data range term');
        }
        // DataIntersectionOf
        const intersectionQuads = this.store.getQuads(term, namedNode(OWL_INTERSECTION_OF), null, null);
        if (intersectionQuads.length > 0) {
            const list = this.parseRdfList(intersectionQuads[0].object);
            const ranges = list.map(t => this.parseDataRange(t));
            if (ranges.length >= 2) {
                return factories.dataIntersectionOf(ranges[0], ranges[1], ...ranges.slice(2));
            }
        }
        // DataUnionOf
        const unionQuads = this.store.getQuads(term, namedNode(OWL_UNION_OF), null, null);
        if (unionQuads.length > 0) {
            const list = this.parseRdfList(unionQuads[0].object);
            const ranges = list.map(t => this.parseDataRange(t));
            if (ranges.length >= 2) {
                return factories.dataUnionOf(ranges[0], ranges[1], ...ranges.slice(2));
            }
        }
        // DataComplementOf
        const complementQuads = this.store.getQuads(term, namedNode(OWL_DATATYPE_COMPLEMENT_OF), null, null);
        if (complementQuads.length > 0) {
            return factories.dataComplementOf(this.parseDataRange(complementQuads[0].object));
        }
        // DataOneOf
        const oneOfQuads = this.store.getQuads(term, namedNode(OWL_ONE_OF), null, null);
        if (oneOfQuads.length > 0) {
            const list = this.parseRdfList(oneOfQuads[0].object);
            const literals = list.filter(t => t.termType === 'Literal').map(t => this.parseLiteral(t));
            if (literals.length >= 1) {
                return factories.dataOneOf(literals[0], ...literals.slice(1));
            }
        }
        // DatatypeRestriction
        const onDatatypeQuads = this.store.getQuads(term, namedNode(OWL_ON_DATATYPE), null, null);
        if (onDatatypeQuads.length > 0) {
            const dt = factories.datatype(fullIRI(onDatatypeQuads[0].object.value));
            const withRestrictionsQuads = this.store.getQuads(term, namedNode(OWL_WITH_RESTRICTIONS), null, null);
            if (withRestrictionsQuads.length > 0) {
                const list = this.parseRdfList(withRestrictionsQuads[0].object);
                const facets = list.map(t => this.parseFacetRestriction(t));
                if (facets.length >= 1) {
                    return factories.datatypeRestriction(dt, facets[0], ...facets.slice(1));
                }
            }
        }
        throw new Error('Unknown data range type');
    }
    parseFacetRestriction(bn) {
        const quads = this.store.getQuads(bn, null, null, null);
        for (const q of quads) {
            if (q.predicate.termType === 'NamedNode' && q.object.termType === 'Literal') {
                return {
                    facet: fullIRI(q.predicate.value),
                    value: this.parseLiteral(q.object)
                };
            }
        }
        throw new Error('Invalid facet restriction');
    }
    // =========================================================================
    // Property Expression Parsing
    // =========================================================================
    parseObjectPropertyExpression(term) {
        if (term.termType === 'NamedNode') {
            return factories.objectProperty(fullIRI(term.value));
        }
        if (term.termType === 'BlankNode') {
            // ObjectInverseOf
            const inverseQuads = this.store.getQuads(term, namedNode(OWL_INVERSE_OF), null, null);
            if (inverseQuads.length > 0 && inverseQuads[0].object.termType === 'NamedNode') {
                return factories.objectInverseOf(factories.objectProperty(fullIRI(inverseQuads[0].object.value)));
            }
        }
        throw new Error('Invalid object property expression');
    }
    parseDataPropertyExpression(term) {
        if (term.termType === 'NamedNode') {
            return factories.dataProperty(fullIRI(term.value));
        }
        throw new Error('Invalid data property expression');
    }
    // =========================================================================
    // Individual Parsing
    // =========================================================================
    parseIndividual(term) {
        if (term.termType === 'NamedNode') {
            return factories.namedIndividual(fullIRI(term.value));
        }
        if (term.termType === 'BlankNode') {
            return factories.anonymousIndividual(term.value);
        }
        throw new Error('Invalid individual term');
    }
    // =========================================================================
    // Literal Parsing
    // =========================================================================
    parseLiteral(lit) {
        if (lit.language) {
            return plainLiteral(lit.value, lit.language);
        }
        if (lit.datatype && lit.datatype.value !== `${XSD_NAMESPACE}string`) {
            return typedLiteral(lit.value, fullIRI(lit.datatype.value));
        }
        return plainLiteral(lit.value);
    }
    // =========================================================================
    // Helper Methods
    // =========================================================================
    parseRdfList(term) {
        const items = [];
        let current = term;
        while (current.termType !== 'NamedNode' || current.value !== RDF_NIL) {
            const firstQuads = this.store.getQuads(current, namedNode(RDF_FIRST), null, null);
            const restQuads = this.store.getQuads(current, namedNode(RDF_REST), null, null);
            if (firstQuads.length === 0 || restQuads.length === 0)
                break;
            items.push(firstQuads[0].object);
            current = restQuads[0].object;
        }
        return items;
    }
    isObjectProperty(term) {
        if (term.termType === 'NamedNode') {
            // OWL object property
            if (this.store.getQuads(term, namedNode(RDF_TYPE), namedNode(OWL_OBJECT_PROPERTY), null).length > 0) {
                return true;
            }
            // RDF property with non-data range (infer as object property)
            if (this.store.getQuads(term, namedNode(RDF_TYPE), namedNode(RDF_PROPERTY), null).length > 0) {
                return !this.hasDataRange(term);
            }
            return false;
        }
        if (term.termType === 'BlankNode') {
            // Could be ObjectInverseOf
            return this.store.getQuads(term, namedNode(OWL_INVERSE_OF), null, null).length > 0;
        }
        return false;
    }
    isDataProperty(term) {
        if (term.termType !== 'NamedNode')
            return false;
        // OWL data property
        if (this.store.getQuads(term, namedNode(RDF_TYPE), namedNode(OWL_DATATYPE_PROPERTY), null).length > 0) {
            return true;
        }
        // RDF property with data range (infer as data property)
        if (this.store.getQuads(term, namedNode(RDF_TYPE), namedNode(RDF_PROPERTY), null).length > 0) {
            return this.hasDataRange(term);
        }
        return false;
    }
    isAnnotationProperty(term) {
        if (term.termType !== 'NamedNode')
            return false;
        const iri = term.value;
        // Check for built-in annotation properties
        if (iri === `${RDFS_NAMESPACE}label` ||
            iri === `${RDFS_NAMESPACE}comment` ||
            iri === `${RDFS_NAMESPACE}seeAlso` ||
            iri === `${RDFS_NAMESPACE}isDefinedBy`) {
            return true;
        }
        return this.store.getQuads(term, namedNode(RDF_TYPE), namedNode(OWL_ANNOTATION_PROPERTY), null).length > 0;
    }
    isDatatype(term) {
        if (term.termType === 'NamedNode') {
            const iri = term.value;
            if (iri.startsWith(XSD_NAMESPACE))
                return true;
            return this.store.getQuads(term, namedNode(RDF_TYPE), namedNode(RDFS_DATATYPE), null).length > 0;
        }
        return false;
    }
    isBuiltInType(term) {
        if (term.termType !== 'NamedNode')
            return false;
        const iri = term.value;
        return iri === OWL_CLASS ||
            iri === OWL_OBJECT_PROPERTY ||
            iri === OWL_DATATYPE_PROPERTY ||
            iri === OWL_ANNOTATION_PROPERTY ||
            iri === OWL_NAMED_INDIVIDUAL ||
            iri === OWL_ONTOLOGY ||
            iri === OWL_RESTRICTION ||
            iri === OWL_ALL_DISJOINT_CLASSES ||
            iri === OWL_ALL_DISJOINT_PROPERTIES ||
            iri === OWL_ALL_DIFFERENT ||
            iri === OWL_NEGATIVE_PROPERTY_ASSERTION ||
            iri === RDFS_DATATYPE;
    }
    termToKey(term) {
        if (term.termType === 'NamedNode')
            return term.value;
        if (term.termType === 'BlankNode')
            return `_:${term.value}`;
        if (term.termType === 'Literal')
            return `"${term.value}"`;
        return term.value;
    }
}
/**
 * Convenience function to parse Turtle string to Ontology.
 *
 * @param turtle - The Turtle content to parse
 * @param options - Optional parsing options including workspace for resolving external references
 */
export async function parseTurtle(turtle, options) {
    const parser = new Owl2TurtleParser();
    return parser.parse(turtle, options);
}
